import { useRef, useEffect } from "react";

// iOS Safari only lets a <video>'s .play() promise resolve when it runs
// either (a) synchronously inside a trusted user-gesture event — touchstart,
// click — or (b) the video was already visible and playing on initial page
// load. An IntersectionObserver callback firing on scroll is NEITHER of
// those, even with muted+playsInline set — iOS silently rejects the play()
// call, which is exactly why a below-the-fold background video just sits
// frozen on its poster frame on iPhone no matter what JS calls .play().
//
// Fix: the first touchstart/click anywhere on the page IS a real user
// gesture. Calling .play() synchronously inside that handler "unlocks"
// media playback for the rest of the session — a well-documented iOS
// behavior. After that one-time unlock, the IntersectionObserver-driven
// play()/pause() on scroll works reliably for every video using this hook.
export function useAutoplayVideo({ threshold = 0.25 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Some mobile browsers only honor autoplay when `muted` is set as an
    // actual DOM property, not just the JSX/HTML attribute — belt and
    // suspenders alongside the `muted` prop already on the element.
    el.muted = true;
    el.defaultMuted = true;

    const tryPlay = () => {
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };

    const unlock = () => {
      tryPlay();
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
    };
    window.addEventListener("touchstart", unlock, { passive: true, once: true });
    window.addEventListener("click", unlock, { once: true });

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          tryPlay();
        } else {
          el.pause();
        }
      },
      { threshold }
    );
    observer.observe(el);

    return () => {
      observer.disconnect();
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
    };
  }, [threshold]);

  return ref;
}
